import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  CircleArrowRight,
  CircleCheck,
  Pencil,
  Send,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDialogState } from "@/shared/lib/useDialogState";
import { RaidDialog } from "@/shared/raid-day/RaidDialog";
import { toDayMonth, toHourMinute, toWeekdayName } from "@/shared/utils/date";
import {
  useCreateBossView,
  useDeleteBossView,
  useDeleteStandIn,
  usePublishBossSetup,
  useUpdateStandIn,
  useUpsertOverride,
} from "./api/setup.mutations";
import {
  nextRaidQueryOptions,
  raidsQueryOptions,
  setupQueryOptions,
} from "./api/setup.queries";
import { BossSetupMatrix } from "./components/BossSetupMatrix";
import { RulesPanel } from "./components/RulesPanel";
import { SetupBoard } from "./components/SetupBoard";
import { StandInDialog } from "./components/StandInDialog";
import { UnavailablePanel } from "./components/UnavailablePanel";
import {
  memberOverrideBody,
  type PlayerChange,
  standInUpdate,
} from "./setup.overrides";
import type { SetupPlayer } from "./setup.types";
import {
  canShowBossViewSwitch,
  resolveSetupView,
  type SetupViewMode,
} from "./setup.view";
import { useAlignActionToContentEdge } from "./useAlignActionToContentEdge";

export function SetupPage({
  raidId,
  canEdit,
  view,
}: {
  raidId?: string;
  canEdit: boolean;
  view?: "bosses";
}) {
  const { data: next } = useSuspenseQuery(nextRaidQueryOptions);
  const effectiveId = raidId ?? next.raidId;
  if (!effectiveId) {
    return (
      <div className="space-y-2">
        <h1 className="text-lg font-semibold">Setup</h1>
        <p className="text-muted-foreground">
          Ingen kommande raid att planera.{" "}
          <Link to="/" className="underline">
            Skapa en raiddag i schemat.
          </Link>
        </p>
      </div>
    );
  }
  return (
    <SetupView
      raidId={effectiveId}
      nextRaidId={next.raidId}
      canEdit={canEdit}
      view={view}
    />
  );
}

function SetupView({
  raidId,
  nextRaidId,
  canEdit,
  view,
}: {
  raidId: string;
  nextRaidId: string | null;
  canEdit: boolean;
  view?: "bosses";
}) {
  const { data } = useSuspenseQuery(setupQueryOptions(raidId));
  const navigate = useNavigate();
  // Server-computed (UTC) — same authority as the write gate, no client clock.
  const editable = canEdit && !data.isPast;
  const requestedView: SetupViewMode = view ?? "setup";
  const resolvedView = resolveSetupView(
    requestedView,
    data.bossContent,
    canEdit,
  );
  // resolveSetupView already collapses to "setup" unless the boss switch is
  // available and requested, so the resolved view is the view we render.
  const showBossSwitch = canShowBossViewSwitch(data.bossContent, canEdit);
  const showingBosses = resolvedView === "bosses";

  const upsertOverride = useUpsertOverride(raidId);
  const updateStandIn = useUpdateStandIn(raidId);
  const deleteStandIn = useDeleteStandIn(raidId);
  const createBossView = useCreateBossView(raidId);
  const deleteBossView = useDeleteBossView(raidId);
  const publishBossSetup = usePublishBossSetup(raidId);
  const [raidDialogOpen, setRaidDialogOpen] = useState(false);
  const [deleteBossConfirmationOpen, setDeleteBossConfirmationOpen] =
    useState(false);
  const [publishConfirmationOpen, setPublishConfirmationOpen] = useState(false);
  // Points at the boss matrix <table> so Publicera can screenshot the full grid.
  const matrixRef = useRef<HTMLTableElement>(null);

  const publish = () => {
    const node = matrixRef.current;
    if (!node) return;
    // The modal closes itself on confirm; the page button shows the spinner.
    publishBossSetup.mutate(node);
  };

  // The one place that decides member vs stand-in: every board/menu edit is a
  // PlayerChange, routed to the right full-state write.
  const applyChange = (p: SetupPlayer, change: PlayerChange) =>
    p.kind === "member"
      ? upsertOverride.mutate({
          userId: p.id,
          ...memberOverrideBody(p, change),
        })
      : updateStandIn.mutate(standInUpdate(p, change));

  // "new" → create; a player → edit.
  const standInDialog = useDialogState<SetupPlayer | "new">();
  const editingPlayer =
    standInDialog.target === "new"
      ? undefined
      : (standInDialog.target ?? undefined);

  const actionRef = useRef<HTMLDivElement>(null);
  useAlignActionToContentEdge(actionRef, { reflowKey: resolvedView });

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Header: the fixed 18rem select (which doubles as the page title) and its
          description sit left, with the next-raid marker beside them; the stand-in
          action is pinned top-right so the rules rail below can top-align with the
          board columns. Wraps on a narrow window. */}
        <div className="space-y-1">
          {/* Next-raid marker sits on top of the select. */}
          {raidId === nextRaidId ? (
            // Status color exception: green confirms "you're planning the right raid".
            <p className="flex items-center gap-1.5 text-sm text-emerald-500">
              <CircleCheck className="size-4" />
              Nästa raid
            </p>
          ) : (
            nextRaidId && (
              <Button
                variant="link"
                size="sm"
                className="h-auto gap-1.5 p-0 has-[svg]:px-0"
                onClick={() =>
                  navigate({
                    to: "/setup",
                    search: { raid: nextRaidId, view },
                  })
                }
              >
                <CircleArrowRight />
                Nästa raid
              </Button>
            )
          )}
          {/* Raid/view selectors + actions wrap on narrow windows. */}
          <div className="flex flex-col gap-3 min-[485px]:flex-row min-[485px]:items-start">
            <div className="flex min-w-0 flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1 min-[485px]:w-72 min-[485px]:flex-none">
                <RaidSelect currentRaidId={raidId} view={view} />
                {/* Always rendered at a fixed one-line height so the content doesn't
                  jump between raids with and without a description. */}
                <p className="min-h-5 truncate text-sm text-muted-foreground/70">
                  {data.raid.description}
                </p>
              </div>
              {showBossSwitch && (
                <SetupViewToggle
                  value={resolvedView}
                  onChange={(nextView) => {
                    navigate({
                      to: "/setup",
                      search: {
                        raid: raidId,
                        view: nextView === "bosses" ? "bosses" : undefined,
                      },
                    });
                  }}
                />
              )}
            </div>
            {/* h-9 keeps the action vertically centered on the select trigger.
                marginRight is set at runtime to align with the content's right edge. */}
            <div
              ref={actionRef}
              className="flex h-9 shrink-0 items-center min-[485px]:ml-auto"
            >
              {editable && (
                <div className="flex w-full flex-wrap gap-2 min-[485px]:w-auto min-[485px]:flex-nowrap">
                  {showingBosses && data.bossContent?.isCreated && (
                    <>
                      <Button
                        size="sm"
                        className="flex-1 min-[485px]:flex-none"
                        loading={publishBossSetup.isPending}
                        onClick={() => setPublishConfirmationOpen(true)}
                      >
                        <Send />
                        Publicera
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1 min-[485px]:flex-none"
                        onClick={() => setDeleteBossConfirmationOpen(true)}
                      >
                        <Trash2 />
                        Ta bort setup per boss
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 min-[485px]:flex-none"
                    onClick={() => setRaidDialogOpen(true)}
                  >
                    <Pencil />
                    Redigera raid
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 min-[485px]:flex-none"
                    onClick={() => standInDialog.openWith("new")}
                  >
                    <UserPlus />
                    Lägg till stand-in
                  </Button>
                </div>
              )}
              {data.isPast && (
                <p className="text-sm text-muted-foreground">
                  Passerad raid – skrivskyddad
                </p>
              )}
            </div>
          </div>
        </div>

        {showingBosses ? (
          data.bossContent?.isCreated ? (
            <BossSetupMatrix
              raidId={raidId}
              data={data}
              editable={editable}
              onChange={applyChange}
              onDeleteStandIn={(id) => deleteStandIn.mutate({ id })}
              onEditStandIn={standInDialog.openWith}
              tableRef={matrixRef}
            />
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6 text-center">
              <Button
                disabled={!editable}
                loading={createBossView.isPending}
                onClick={() => createBossView.mutate()}
              >
                Skapa setup per boss
              </Button>
            </div>
          )
        ) : (
          /* Board + rules rail wrap as whole units: the rail keeps its width and
             drops before the board's columns run out of room. */
          <div className="flex flex-col gap-4 min-[485px]:flex-row min-[485px]:flex-wrap min-[485px]:items-start min-[485px]:gap-x-8 min-[485px]:gap-y-4">
            <SetupBoard
              players={data.players}
              editable={editable}
              onChange={applyChange}
              onDeleteStandIn={(id) => deleteStandIn.mutate({ id })}
              onEditStandIn={standInDialog.openWith}
            />
            <aside data-col className="min-[485px]:w-48 min-[485px]:shrink-0">
              <RulesPanel players={data.players} />
            </aside>
          </div>
        )}

        {/* Shared "Ej med" panel — the same component under both views. Hidden in
            the boss view until the boss content is created (the CTA screen has no
            setup context to attach it to). */}
        {(showingBosses ? data.bossContent?.isCreated : true) && (
          <UnavailablePanel
            players={data.players}
            absent={data.absent}
            editable={editable}
            onChange={applyChange}
            onDeleteStandIn={(id) => deleteStandIn.mutate({ id })}
            onEditStandIn={standInDialog.openWith}
          />
        )}
      </div>

      {editable && (
        <>
          <RaidDialog
            open={raidDialogOpen}
            onOpenChange={setRaidDialogOpen}
            date={data.raid.date}
            raid={{
              ...data.raid,
              configuredRaidIds:
                data.bossContent?.raids.map((raid) => raid.id) ?? [],
            }}
          />
          <StandInDialog
            open={standInDialog.open}
            onOpenChange={standInDialog.setOpen}
            raidId={raidId}
            player={editingPlayer}
          />
          <AlertDialog
            open={publishConfirmationOpen}
            onOpenChange={setPublishConfirmationOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Publicera setup per boss?</AlertDialogTitle>
                <AlertDialogDescription>
                  En bild av setupen skickas till Discord.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction onClick={publish}>
                  <Send />
                  Publicera
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog
            open={deleteBossConfirmationOpen}
            onOpenChange={setDeleteBossConfirmationOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ta bort setup per boss?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dina boss-relaterade val kommer raderas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  disabled={deleteBossView.isPending}
                  onClick={() => deleteBossView.mutate()}
                >
                  <Trash2 />
                  Ta bort
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

// Raid-switcher dropdown: picking a raid navigates to its setup.
function RaidSelect({
  currentRaidId,
  view,
}: {
  currentRaidId: string;
  view?: "bosses";
}) {
  const { data: raids } = useSuspenseQuery(raidsQueryOptions);
  const navigate = useNavigate();
  return (
    <Select
      value={currentRaidId}
      onValueChange={(id) =>
        navigate({ to: "/setup", search: { raid: id, view } })
      }
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {raids.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r.title} ({toWeekdayName(r.date).toLowerCase()}{" "}
            {toDayMonth(r.date)} {toHourMinute(r.startTime)})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SetupViewToggle({
  value,
  onChange,
}: {
  value: SetupViewMode;
  onChange: (value: SetupViewMode) => void;
}) {
  const nextView = value === "bosses" ? "setup" : "bosses";

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9 shrink-0"
      onClick={() => onChange(nextView)}
    >
      {nextView === "bosses" ? "Setup per boss" : "Setup"}
    </Button>
  );
}
